import { type CSSProperties, type ReactNode, type ButtonHTMLAttributes, forwardRef } from 'react';

// ─── Types ───

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  /** Visual variant */
  variant?: 'gradient' | 'solid' | 'outline' | 'ghost';
  /** Size preset */
  size?: 'sm' | 'md' | 'lg';
  /** Icon before the label */
  leftIcon?: ReactNode;
  /** Icon after the label */
  rightIcon?: ReactNode;
  /** Extend/override styles */
  style?: CSSProperties;
}

// ─── Size Map ───

const sizes: Record<string, { height: number; padding: string }> = {
  sm: { height: 32, padding: '0 var(--sp-3, 12px)' },
  md: { height: 36, padding: '0 var(--sp-4, 16px)' },
  lg: { height: 42, padding: '0 var(--sp-6, 24px)' },
};

// ─── Variant Styles ───

function variantStyles(variant: string, disabled: boolean): CSSProperties {
  const base: CSSProperties = {
    border: 'none',
    background: 'transparent',
    color: 'var(--text, #111827)',
  };

  switch (variant) {
    case 'gradient':
      return {
        ...base,
        background:
          'var(--gradient-cta, var(--gradient-primary, linear-gradient(135deg, #2563eb, #1d4ed8)))',
        color: 'var(--text-on-primary, #ffffff)',
        opacity: disabled ? 0.5 : 1,
      };
    case 'solid':
      return {
        ...base,
        background: 'var(--color-primary, #2563eb)',
        color: 'var(--text-on-primary, #ffffff)',
        opacity: disabled ? 0.5 : 1,
      };
    case 'outline':
      return {
        ...base,
        border: '1px solid var(--border, #e2e8f0)',
        background: 'transparent',
        color: 'var(--text-muted, #64748b)',
        opacity: disabled ? 0.5 : 1,
      };
    case 'ghost':
      return {
        ...base,
        background: 'transparent',
        color: 'var(--text-muted, #64748b)',
        opacity: disabled ? 0.5 : 1,
      };
    default:
      return base;
  }
}

// ─── Component ───

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'solid', size = 'md', leftIcon, rightIcon, disabled, children, style, ...rest },
  ref,
) {
  const s = sizes[size];
  const v = variantStyles(variant, !!disabled);

  return (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-2, 8px)',
        height: s.height,
        padding: s.padding,
        borderRadius: 'var(--radius-md, 10px)',
        fontSize: 'var(--font-size-base, 14px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        lineHeight: 'var(--line-height-base, 20px)',
        letterSpacing: '-0.15px',
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'filter 150ms ease, opacity 150ms ease',
        boxSizing: 'border-box',
        flexShrink: 0,
        ...v,
        ...style,
      }}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
